/**
 * Cœur réseau du client, indépendant de l'environnement : il tourne dans un
 * Web Worker (cas normal) ou sur le fil principal (repli). Il garde la
 * connexion WebSocket, rejoue le hello, mesure la latence, vérifie la chaîne
 * des deltas (base == dernier tick reçu) et demande un resync en cas de trou.
 * Le rendu ne peut donc jamais retarder la réception ni les pings.
 */
import {
  PROTOCOL_VERSION,
  type AckMsg,
  type Command,
  type DeltaMsg,
  type ErrorMsg,
  type ServerMessage,
  type SnapshotMsg,
  type WelcomeMsg,
} from '@snowchain/shared';

/** world-error : partie introuvable, complète ou version différente ; on ne retente pas. */
export type NetStatus = 'connecting' | 'online' | 'offline' | 'auth-error' | 'world-error';

export type NetEvent =
  | { k: 'status'; status: NetStatus; detail?: string; retryInMs?: number }
  | { k: 'welcome'; msg: WelcomeMsg }
  | { k: 'snapshot'; msg: SnapshotMsg; at: number }
  | { k: 'delta'; msg: DeltaMsg; at: number }
  | { k: 'ack'; msg: AckMsg }
  | { k: 'error'; msg: ErrorMsg }
  | { k: 'rtt'; rtt: number }
  | { k: 'traffic'; bytesPerSec: number; msgsPerSec: number };

export type NetCommand =
  | { k: 'connect'; url: string; token: string; world?: string }
  | { k: 'cmd'; seq: number; cmd: Command }
  | { k: 'close' };

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export class NetCore {
  private ws: WebSocket | null = null;
  private url = '';
  private token = '';
  private world: string | undefined;
  private lastTick = -1;
  private awaitingSnapshot = true;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private trafficTimer: ReturnType<typeof setInterval> | null = null;
  private retry = 0;
  private closedByUser = false;
  /** Dernier refus du serveur (partie complète, introuvable...), affiché à la fermeture. */
  private lastError = '';
  private pingId = 0;
  private bytes = 0;
  private msgs = 0;
  /** Commandes émises hors ligne : renvoyées à la reconnexion. */
  private outbox: { seq: number; cmd: Command }[] = [];

  constructor(private readonly emit: (e: NetEvent) => void) {}

  handle(c: NetCommand): void {
    if (c.k === 'connect') {
      this.url = c.url;
      this.token = c.token;
      this.world = c.world;
      this.closedByUser = false;
      this.open();
    } else if (c.k === 'cmd') {
      if (this.ws && this.ws.readyState === WebSocket.OPEN && !this.awaitingSnapshot) this.ws.send(JSON.stringify({ t: 'cmd', seq: c.seq, cmd: c.cmd }));
      else this.outbox.push({ seq: c.seq, cmd: c.cmd });
    } else if (c.k === 'close') {
      this.closedByUser = true;
      this.ws?.close(1000, 'fermeture');
    }
  }

  private open(): void {
    this.emit({ k: 'status', status: 'connecting' });
    this.lastTick = -1;
    this.awaitingSnapshot = true;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ t: 'hello', v: PROTOCOL_VERSION, token: this.token, world: this.world }));
    };
    ws.onmessage = (ev) => this.onMessage(ev.data as string);
    ws.onclose = (ev) => this.onClose(ev.code, ev.reason);
    ws.onerror = () => {
      /* onclose suit */
    };
    if (!this.pingTimer) this.pingTimer = setInterval(() => this.ping(), 2000);
    if (!this.trafficTimer)
      this.trafficTimer = setInterval(() => {
        this.emit({ k: 'traffic', bytesPerSec: this.bytes, msgsPerSec: this.msgs });
        this.bytes = 0;
        this.msgs = 0;
      }, 1000);
  }

  private ping(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: 'ping', id: ++this.pingId, ct: now() }));
  }

  private onMessage(data: string): void {
    this.bytes += data.length;
    this.msgs++;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(data) as ServerMessage;
    } catch {
      return;
    }
    const at = now();
    switch (msg.t) {
      case 'welcome':
        this.retry = 0;
        this.emit({ k: 'welcome', msg });
        break;
      case 'snapshot':
        this.lastTick = msg.clock.tick;
        this.awaitingSnapshot = false;
        this.emit({ k: 'snapshot', msg, at });
        this.emit({ k: 'status', status: 'online' });
        this.flushOutbox();
        this.ping();
        break;
      case 'delta':
        if (this.awaitingSnapshot) return;
        if (msg.base !== this.lastTick) {
          // Trou dans la chaîne : on ignore la suite jusqu'au prochain snapshot.
          this.awaitingSnapshot = true;
          this.ws?.send(JSON.stringify({ t: 'resync' }));
          return;
        }
        this.lastTick = msg.clock.tick;
        this.emit({ k: 'delta', msg, at });
        break;
      case 'ack':
        this.emit({ k: 'ack', msg });
        break;
      case 'pong':
        this.emit({ k: 'rtt', rtt: Math.max(0, at - msg.ct) });
        break;
      case 'error':
        if (msg.code === 'world' || msg.code === 'protocol') this.lastError = msg.message;
        this.emit({ k: 'error', msg });
        break;
    }
  }

  private flushOutbox(): void {
    const pending = this.outbox;
    this.outbox = [];
    for (const p of pending) this.ws?.send(JSON.stringify({ t: 'cmd', seq: p.seq, cmd: p.cmd }));
  }

  private onClose(code: number, reason: string): void {
    this.ws = null;
    if (this.closedByUser) {
      this.emit({ k: 'status', status: 'offline', detail: 'Déconnecté' });
      return;
    }
    if (code === 4004) {
      this.emit({ k: 'status', status: 'auth-error', detail: 'Session expirée' });
      return;
    }
    // Version différente, partie supprimée ou complète : réessayer ne changerait rien.
    if (code === 4003 || code === 4005 || code === 4006) {
      this.emit({ k: 'status', status: 'world-error', detail: this.lastError || reason || 'Partie indisponible' });
      return;
    }
    // Reconnexion avec attente exponentielle (0,5 s ... 8 s).
    const delay = Math.min(8000, 500 * 2 ** this.retry);
    this.retry = Math.min(this.retry + 1, 6);
    this.emit({ k: 'status', status: 'offline', detail: reason || 'Connexion perdue', retryInMs: delay });
    setTimeout(() => {
      if (!this.closedByUser) this.open();
    }, delay);
  }
}
